import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import './_subscriptions.scss'

import { IconPlusInBox } from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { CreateListModal } from 'components-ref/organisms'
import { usePageTitle } from 'context/pageTitleContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import SubscriptionsList from 'features/SubscriptionsList/SubscriptionsList'
import { AllIcons } from 'helpers/allIcons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
  deleteSubscriptionList,
  getSubscriptionLists
} from 'services/endpoints/subscriptionList'
import { useAuthStore, useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import {
  type OrderType,
  type SortType,
  useSubscriptionListsCacheStore
} from 'store/subscriptionListsCacheStore'

const useSortSubsTop = () => {
  const { t } = useTranslation()

  return [
    { value: 'recently_added', name: t('created') },
    { value: 'name', name: t('name') }
  ]
}

const useSubsOrder = () => {
  const { t } = useTranslation()

  return [
    { value: 'asc', name: t('ascending') },
    { value: 'desc', name: t('descending') }
  ]
}

const noop = () => {}

const isAllList = (item: any) => {
  const d = item?.data ?? item
  const name = String(d?.name ?? '').trim().toLowerCase()
  const slug = String(d?.slug ?? '').trim().toLowerCase()

  return d?.is_all === true || slug === 'all' || name === 'all'
}

function useFilterQuery() {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}

export const Lists = () => {
  const { setTitle } = usePageTitle()
  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const { t } = useTranslation()

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedSort, setSelectedSort] = useState<SortType>('name')
  const [selectedOrder, setSelectedOrder] = useState<OrderType>('asc')
  const [activeFilter, setActiveFilter] = useState('active')

  const [isViewsOpen, setIsViewsOpen] = useState<boolean>(false)
  const [listNames, setListNames] = useState<string[]>([])
  const [lists, setLists] = useState<any[]>([])
  const [countsById, setCountsById] = useState<Record<string, number>>({})
  const [listId, setListId] = useState<any>(null)
  const [listChangeName, setListChangeName] = useState<any>(null)

  const userData = useUserProfile()

  const filter = useFilterQuery()
  const type = filter.get('type')
  const sort = filter.get('sort') as SortType | null
  const order = filter.get('order') as OrderType | null

  const createNewList = () => setIsViewsOpen(true)

  const closeModal = () => {
    setIsViewsOpen(false)
    setListId('')
    setListChangeName('')
  }

  const getEntry = useSubscriptionListsCacheStore(s => s.getEntry)
  const setEntry = useSubscriptionListsCacheStore(s => s.setEntry)
  const removeListFromCache = useSubscriptionListsCacheStore(s => s.removeList)

  useEffect(() => {
    setTitle(t('lists'))
  }, [setTitle, t])

  useEffect(() => {
    if (type) setActiveFilter(type)
    if (sort) setSelectedSort(sort)
    if (order) setSelectedOrder(order)
  }, [type, sort, order])

  useEffect(() => {
    const cached = getEntry(selectedSort, selectedOrder)

    if (cached && Array.isArray(cached.lists) && cached.lists.length > 0) {
      setLists(cached.lists)
      setCountsById(cached.countsById ?? {})
      setListNames(cached.lists.map((l: any) => l?.data?.name).filter(Boolean))
      return
    }

    const sourceLists = userData?.selected_lists ?? []

    const tmp = sourceLists.map((d: any) => ({
      data: {
        id: d?.id,
        name: d?.name,
        slug: d?.slug,
        is_all: d?.is_all,
        selected: d?.selected,
        order: d?.order,
        default: d?.default,
        created_at: d?.created_at ?? d?.createdAt
      }
    }))

    setLists(tmp)
    setCountsById({})
    setListNames(tmp.map((l: any) => l?.data?.name).filter(Boolean))
  }, [getEntry, selectedSort, selectedOrder, userData])

  useQuery(
    ['subscription-lists', selectedSort, selectedOrder],
    async () => {
      const res = await getSubscriptionLists(selectedSort, selectedOrder, 1)
      const rows = res?.page?.data?.data ?? []

      return rows
    },
    {
      enabled: Boolean(userData?.id),
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: 20_000,
      onSuccess: (rows: any[]) => {
        const nextCounts: Record<string, number> = {}

        for (const row of rows) {
          const id = row?.id

          if (typeof id === 'string' && id) {
            const n = Number(row?.count)
            nextCounts[id] = Number.isFinite(n) ? n : 0
          }
        }

        const nextLists = rows.map((r: any) => ({
          data: {
            id: r?.id,
            name: r?.name,
            slug: r?.slug,
            is_all: r?.is_all,
            selected: r?.selected,
            order: r?.order,
            default: r?.default,
            created_at: r?.created_at ?? r?.createdAt
          }
        }))

        setCountsById(nextCounts)

        if (Array.isArray(nextLists) && nextLists.length > 0) {
          setLists(nextLists)
          setListNames(nextLists.map((l: any) => l?.data?.name).filter(Boolean))
        }

        setEntry(selectedSort, selectedOrder, {
          lists: nextLists,
          countsById: nextCounts,
          updatedAt: Date.now()
        })
      }
    }
  )

  const finalLists = useMemo(() => {
    if (!Array.isArray(lists) || lists.length === 0) return []

    const allItem = lists.find(isAllList) || null
    const rest = lists.filter(l => !isAllList(l))

    const term = searchTerm.trim().toLowerCase()

    const filtered = rest.filter(item =>
      term ? (item?.data?.name ?? '').toLowerCase().includes(term) : true
    )

    filtered.sort((a, b) => {
      if (selectedSort === 'name') {
        const an = (a?.data?.name ?? '').toLowerCase()
        const bn = (b?.data?.name ?? '').toLowerCase()

        if (an < bn) return selectedOrder === 'asc' ? -1 : 1
        if (an > bn) return selectedOrder === 'asc' ? 1 : -1

        return 0
      }

      const aKey = a?.data?.created_at ?? a?.data?.createdAt ?? a?.data?.id ?? 0
      const bKey = b?.data?.created_at ?? b?.data?.createdAt ?? b?.data?.id ?? 0

      if (aKey < bKey) return selectedOrder === 'asc' ? -1 : 1
      if (aKey > bKey) return selectedOrder === 'asc' ? 1 : -1

      return 0
    })

    return allItem ? [allItem, ...filtered] : filtered
  }, [lists, searchTerm, selectedSort, selectedOrder])

  const deleteList = async (id: string) => {
    const target = lists.find(l => l?.data?.id === id)

    if (target && isAllList(target)) {
      addToast('error', t('You cannot delete the All list'))
      return
    }

    try {
      await deleteSubscriptionList(id)

      setLists(prev =>
        prev.filter(list => list?.data?.id !== id)
      )

      removeListFromCache(id)

      const prevProfile = useAuthStore.getState().userProfile

      if (prevProfile) {
        const updatedSelectedLists = (
          prevProfile.selected_lists ?? []
        ).filter((list: any) => list.id !== id)

        useAuthStore.getState().setUserProfile({
          selected_lists: updatedSelectedLists
        })

        useProfileCacheStore.getState().setProfile(
          prevProfile.username,
          {
            ...prevProfile,
            selected_lists: updatedSelectedLists
          },
          prevProfile.id
        )
      }

      queryClient.invalidateQueries(['subscription-lists'])
    } catch (error) {
      console.error(`Failed to delete list with ID ${id}`, error)

      addToast(
        'error',
        t('Failed to delete the list. Please try again.')
      )
    }
  }

  const editList = (id: string, name: string) => {
    const target = lists.find(l => l?.data?.id === id)

    if (target && isAllList(target)) {
      addToast('error', t('You cannot edit the All list'))
      return
    }

    setIsViewsOpen(true)
    setListId(id)
    setListChangeName(name)
  }

  return (
    <div className="subscriptionsLists__content__wrapper">
      {isViewsOpen && (
        <CreateListModal
          listNames={listNames}
          onClose={closeModal}
          setLists={setLists}
          lists={lists}
          listId={listId}
          listChangeName={listChangeName}
        />
      )}

      <WithHeaderSection
        customClass="subscriptionlists-whs"
        headerSection={
          <LayoutHeader
            type="search-with-buttons"
            searchValue={searchTerm}
            searchFn={(term: string) => setSearchTerm(term)}
            clearFn={() => setSearchTerm('')}
            additionalProps={{ placeholder: t('searchLists') }}
            buttons={[
              {
                type: 'cta',
                icon: AllIcons.listblue,
                action: () => navigate('/subscriptions')
              },
              {
                type: 'sort',
                elements: {
                  first_section: useSortSubsTop(),
                  second_section: useSubsOrder()
                },
                selectedSort: { selectedSort },
                selectedOrder: { selectedOrder }
              }
            ]}
            applyFn={(val: string, val1?: string) => {
              if (val) setSelectedSort(val as SortType)
              if (val1) setSelectedOrder(val1 as OrderType)
            }}
          />
        }
      >
        <div className="subscriptionsLists">
          <div className="subscriptionsLists__list">
            {finalLists?.map((fans, key: number) => {
              const all = isAllList(fans)
              const safeOnDelete = all ? noop : deleteList
              const safeOnEdit = all ? noop : editList

              return (
                <div key={key} className="subscriptionsLists__list__item">
                  <SubscriptionsList
                    title={fans.data.name}
                    fans={{
                      avatars: [],
                      count: countsById[fans.data.id] ?? 0,
                      id: fans.data.id,
                      data: fans.data
                    }}
                    onDelete={safeOnDelete}
                    onEdit={safeOnEdit}
                    id={fans.data.id}
                    name={fans.data.name}
                    showZeroCount
                  />
                </div>
              )
            })}
          </div>

          <ActionCard
            icon={<IconPlusInBox width="22" height="22" />}
            iconSize="small"
            text={t('addNewList')}
            addNewText
            clickFn={createNewList}
          />
        </div>
      </WithHeaderSection>
    </div>
  )
}